<script setup lang="ts">
/**
 * Pagination — page picker for tables.
 *
 * Mirrors the prototype's .pagination block: "Showing 1–9 of N" on
 * the left, a page strip on the right with prev/next arrows.
 *
 * Page numbers are computed with a sliding window: the current page
 * stays roughly centred when there are many pages, with first/last
 * always visible. Ellipsis tokens compact the gap.
 *
 * Page size is fixed by the consumer (typically TanStack Table); we
 * don't render a size picker in Sprint 1 — that lands in Sprint 2's
 * DataTable wrapper.
 */
import { computed } from 'vue'
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

interface Props {
  /** Zero-indexed current page (TanStack/Spring convention). */
  page: number
  /** Items per page. */
  size: number
  /** Total item count across all pages. */
  total: number
  /** Max numeric buttons (excluding ellipsis); defaults to 5. */
  visibleCount?: number
}

const props = withDefaults(defineProps<Props>(), { visibleCount: 5 })

defineEmits<{
  'update:page': [page: number]
}>()

const { t } = useI18n()

const totalPages = computed(() =>
  Math.max(1, Math.ceil(props.total / props.size)),
)

const firstItem = computed(() =>
  props.total === 0 ? 0 : props.page * props.size + 1,
)
const lastItem = computed(() =>
  Math.min((props.page + 1) * props.size, props.total),
)

/**
 * Compute the page-number tokens to render. Returns numbers and the
 * literal '…' string. Always includes page 0 and the last page;
 * fills the middle with a window around the current page.
 */
const pageTokens = computed<Array<number | '…'>>(() => {
  const total = totalPages.value
  const window = props.visibleCount
  if (total <= window + 2) {
    // Few enough pages to render all without ellipsis
    return Array.from({ length: total }, (_, i) => i)
  }

  const tokens: Array<number | '…'> = [0]
  const half = Math.floor(window / 2)
  let start = Math.max(1, props.page - half)
  let end = Math.min(total - 2, start + window - 1)
  // Re-anchor start if end hit the cap
  if (end - start < window - 1) {
    start = Math.max(1, end - window + 1)
  }

  if (start > 1) tokens.push('…')
  for (let p = start; p <= end; p++) tokens.push(p)
  if (end < total - 2) tokens.push('…')
  tokens.push(total - 1)
  return tokens
})
</script>

<template>
  <div
    class="flex items-center justify-between gap-3 px-4 py-2.5
           border-t border-border text-xs text-text-3"
  >
    <span>
      {{
        total === 0
          ? t('pagination.empty')
          : t('pagination.range', {
              from: firstItem,
              to: lastItem,
              total,
            })
      }}
    </span>

    <div class="flex items-center gap-1" role="navigation" aria-label="Pagination">
      <!-- Prev -->
      <button
        type="button"
        class="w-7 h-7 flex items-center justify-center rounded-sm
               text-text-3 hover:text-text-1 hover:bg-surface-2
               transition-colors disabled:opacity-30 disabled:cursor-not-allowed
               focus-visible:outline-none focus-visible:shadow-focus"
        :disabled="page === 0"
        :aria-label="t('pagination.prev')"
        @click="$emit('update:page', page - 1)"
      >
        <ChevronLeft :size="14" />
      </button>

      <!-- Page numbers -->
      <template v-for="(tok, i) in pageTokens" :key="i">
        <span
          v-if="tok === '…'"
          class="w-7 h-7 flex items-center justify-center text-text-5"
          aria-hidden="true"
        >…</span>
        <button
          v-else
          type="button"
          :class="[
            'w-7 h-7 flex items-center justify-center text-xs font-mono',
            'rounded-sm transition-colors',
            'focus-visible:outline-none focus-visible:shadow-focus',
            tok === page
              ? 'bg-brand-soft text-text-1 border border-brand-line'
              : 'text-text-3 hover:text-text-1 hover:bg-surface-2',
          ]"
          :aria-current="tok === page ? 'page' : undefined"
          @click="$emit('update:page', tok)"
        >{{ tok + 1 }}</button>
      </template>

      <!-- Next -->
      <button
        type="button"
        class="w-7 h-7 flex items-center justify-center rounded-sm
               text-text-3 hover:text-text-1 hover:bg-surface-2
               transition-colors disabled:opacity-30 disabled:cursor-not-allowed
               focus-visible:outline-none focus-visible:shadow-focus"
        :disabled="page >= totalPages - 1"
        :aria-label="t('pagination.next')"
        @click="$emit('update:page', page + 1)"
      >
        <ChevronRight :size="14" />
      </button>
    </div>
  </div>
</template>
